import { useState } from 'react';
import type { FormEvent } from 'react';
import { Button, Field, TextInput } from '@salonomy/ui';
import { useRequestPasswordReset } from '../data/auth-recovery.js';
import { ApiClientError } from '../lib/api.js';

/**
 * "Forgot your password?" (pre-session). Collects an email and POSTs /v1/auth/password/reset-request.
 *
 * Anti-enumeration is the whole point: the API answers 200 {ok:true} whether or not the address is
 * known, so this page shows ONE neutral confirmation either way and never reveals account existence.
 * We only surface an error for an actual transport/validation failure (e.g. a malformed address the
 * API rejects), not for "no such account" — there is no such signal to surface.
 */
export function ForgotPasswordPage() {
  const request = useRequestPasswordReset();
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await request.mutateAsync({ email: email.trim() });
      setSent(true);
    } catch (err) {
      setError(
        err instanceof ApiClientError
          ? err.message
          : 'Could not send the reset email. Check your connection.',
      );
    }
  }

  return (
    <div className="login">
      <div className="login__card">
        <div className="login__mark">
          Salonomy<span style={{ color: 'var(--slm-cobalt)' }}>.</span>
        </div>
        <div className="login__sub">Reset your password.</div>
        {sent ? (
          <div className="login__form">
            <div className="login__sub">
              If an account exists for that email, we’ve sent a link to reset your password. The
              link expires in 1 hour.
            </div>
            <a
              href={import.meta.env.BASE_URL}
              style={{ fontSize: 13, fontWeight: 600, color: 'var(--slm-cobalt)' }}
            >
              Back to sign in →
            </a>
          </div>
        ) : (
          <form className="login__form" onSubmit={onSubmit}>
            {error ? <div className="login__error">{error}</div> : null}
            <Field label="Email" htmlFor="email">
              <TextInput
                id="email"
                type="email"
                autoComplete="username"
                required
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@salon.co.uk"
              />
            </Field>
            <Button type="submit" size="lg" block disabled={request.isPending}>
              {request.isPending ? 'Sending…' : 'Email me a reset link'}
            </Button>
            <a
              href={import.meta.env.BASE_URL}
              style={{ fontSize: 13, fontWeight: 600, color: 'var(--slm-cobalt)' }}
            >
              Back to sign in →
            </a>
          </form>
        )}
      </div>
    </div>
  );
}
